import Utils from '../utils/utils';

const waitTransitionEnd = (el) =>
    new Promise((res) => {
        const onEnd = (e) => {
            if (e.target !== el) return;
            if (e.propertyName !== 'transform') return;
            el.removeEventListener('transitionend', onEnd);
            res();
        };
        el.addEventListener('transitionend', onEnd);
    });

export default class PageTransition {
    constructor(app) {
        this.app = app;
        this.el = document.getElementById('page-transition');
        this._busy = false;
        this.delayOut = 120;
    }

    async show() {
        if (this._busy) return;
        this._busy = true;

        this.el.classList.remove('is-reset');
        document.documentElement.classList.add('is-transitioning');

        await waitTransitionEnd(this.el);

        this.app?.emit?.('enable-scroll');

        const lenis = this.app?.$scroll;
        try {
            if (lenis?.scrollTo) {
                lenis.scrollTo(0, { immediate: true, duration: 0 });
                await new Promise(r => requestAnimationFrame(r));
                window.scrollTo(0, 0);
            } else {
                const html = document.documentElement;
                const prev = html.style.scrollBehavior;
                html.style.scrollBehavior = 'auto';
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                html.style.scrollBehavior = prev;
            }
        } catch (e) {
            window.scrollTo(0, 0);
        }
        this.app?.emit?.('disable-scroll');
    }

    async hide() {
        if (this.delayOut) {
            await new Promise((r) => setTimeout(r, this.delayOut));
        }

        document.documentElement.classList.add('is-transition-done');
        await waitTransitionEnd(this.el);

        document.documentElement.classList.remove('is-transitioning', 'is-transition-done');

        this.el.classList.add('is-reset');
        void this.el.offsetHeight;
        requestAnimationFrame(() => this.el.classList.remove('is-reset'));

        this.app?.emit?.('enable-scroll');
        this._busy = false;

        window.dispatchEvent(new CustomEvent('page-transition:done'));
    }

    async updateMenuAndPopinsBeforeTransition(data) {
        try {
            const nextUrl = data?.next?.url?.href || data?.url?.href || this._nextUrl;
            if (!nextUrl) {
                return;
            }

            const response = await fetch(nextUrl, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!response.ok) {
                return;
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const canonicalLink = doc.querySelector('link[rel="canonical"]');
            this._nextCanonical = canonicalLink ? canonicalLink.getAttribute('href') : null;
            const htmlEl = doc.documentElement;
            this._nextLang = htmlEl.getAttribute('lang') || null;
            this._nextDir = htmlEl.getAttribute('dir') || null;

            const newMenu = doc.querySelector('.c-site-menu');
            const currentMenu = document.querySelector('.c-site-menu');

            if (newMenu && currentMenu) {
                const wasOpened = currentMenu.classList.contains('opened');
                const burgerWasActive = currentMenu.querySelector('.c-site-menu__burger')?.classList.contains('active');

                currentMenu.outerHTML = newMenu.outerHTML;

                if (this.app.$menu) {
                    this.app.$menu.refresh();

                    const updatedMenu = document.querySelector('.c-site-menu');
                    if (wasOpened && updatedMenu) {
                        updatedMenu.classList.add('opened');
                    }
                    if (burgerWasActive) {
                        const burger = updatedMenu?.querySelector('.c-site-menu__burger');
                        if (burger) burger.classList.add('active');
                    }
                }
            }

            const newFooter = doc.querySelector('.c-site-footer');
            const currentFooter = document.querySelector('.c-site-footer');

            if (newFooter && currentFooter) {
                if (this.app.components) {
                    const footerComponent = this.app.components.find(component => {
                        return component.$el && component.$el.classList.contains('c-site-footer');
                    });

                    if (footerComponent && footerComponent.destroy) {
                        footerComponent.destroy();
                    }

                    this.app.components = this.app.components.filter(component => {
                        return !(component.$el && component.$el.classList.contains('c-site-footer'));
                    });
                }

                currentFooter.outerHTML = newFooter.outerHTML;
            }

            const newPopins = doc.querySelectorAll('.c-form-popin');
            const currentPopinsContainer = document.querySelector('body');

            if (newPopins.length > 0 && currentPopinsContainer) {
                if (this.app.components) {
                    const popinComponents = this.app.components.filter(component => {
                        return component.$el && component.$el.classList.contains('c-form-popin');
                    });

                    popinComponents.forEach(component => {
                        if (component.destroy) {
                            component.destroy();
                        }
                    });

                    this.app.components = this.app.components.filter(component => {
                        return !(component.$el && component.$el.classList.contains('c-form-popin'));
                    });
                }

                document.querySelectorAll('.c-form-popin').forEach(popin => {
                    popin.remove();
                });

                newPopins.forEach(popin => {
                    currentPopinsContainer.appendChild(popin.cloneNode(true));
                });
            } else if (newPopins.length === 0) {
                if (this.app.components) {
                    const popinComponents = this.app.components.filter(component => {
                        return component.$el && component.$el.classList.contains('c-form-popin');
                    });

                    popinComponents.forEach(component => {
                        if (component.destroy) {
                            component.destroy();
                        }
                    });

                    this.app.components = this.app.components.filter(component => {
                        return !(component.$el && component.$el.classList.contains('c-form-popin'));
                    });
                }

                document.querySelectorAll('.c-form-popin').forEach(popin => {
                    popin.remove();
                });
            }
        } catch (error) {
            // Silent fail
        }
    }
}
